'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, { useEffect, useRef, useState } from 'react';

import cx from 'clsx';
import { m, useAnimation, useSpring } from 'framer-motion';
import dynamic from 'next/dynamic';
import NextImage from 'next/image';
import PropTypes from 'prop-types';

import { graphRequest } from 'services/contentful';

import Spinner from 'components/ui/Spinner';

import useMediaQuery from 'hooks/useMediaQuery';
import {
    assetFragment,
    ctaFragment,
    iconFragment,
    linkFragment,
} from 'lib/graphql/fragments';
import { getColor } from 'utils';
import { blockRootProps } from 'utils';
import { addProtocol } from 'utils/url';

import PairingWheelContent from './PairingWheelContent';
import PairingWheelOverlay from './PairingWheelOverlay';

import styles from './BlockPairingWheel.module.scss';

const PairingWheel = dynamic(() => import('./PairingWheel'), {
    loading: () => <div>Loading...</div>,
    ssr: false,
});

// GraphQL query for fetching a BlockPairingWheel by ID (client-side)
const pairingWheelQuery = /* GraphQL */ `
    ${assetFragment}
    ${ctaFragment}
    ${linkFragment}
    ${iconFragment}

    query getBlock($id: String!, $preview: Boolean = false) {
        blockPairingWheel(preview: $preview, id: $id) {
            __typename
            entryTitle
            eyebrow
            headline {
                json
            }
            copy {
                json
            }
            image {
                ...Asset
            }
            itemsCollection {
                items {
                    eyebrow
                    headline {
                        json
                    }
                    copy {
                        json
                    }
                    image {
                        ...Asset
                    }
                }
            }
            revealHeadline {
                json
            }
            revealCopy {
                json
            }
            viewGuideCta {
                ... on Cta {
                    ...CTA
                }
            }
            pairAgainCtaText
            revealImage {
                ...Asset
            }
            primaryColor
            secondaryColor
            loadingIcon {
                ... on Icon {
                    ...Icon
                }
            }
            loadingMessage1 {
                json
            }
            loadingMessage2 {
                json
            }
            loadingMessage3 {
                json
            }
            loadingMessage4 {
                json
            }
            nextPairingWheel {
                sys {
                    id
                }
            }
        }
    }
`;

// Configuration constants for wheel clapper behavior
const CLAPPER_CONFIG = {
    // Speed thresholds (degrees per second)
    HIGH_SPEED_THRESHOLD: 300,
    MEDIUM_SPEED_THRESHOLD: 50,

    // Spring configuration
    SPRING_STIFFNESS: 700,
    SPRING_DAMPING: 30,

    // Animation angles (in degrees)
    HIGH_SPEED: {
        BASE_POSITION: 15, // Base pushed position at high speeds
        BOBBLE_AMOUNT: 10, // How much it bobbles at each section
    },
    MEDIUM_SPEED: {
        MAX_BOUNCE: 25, // Maximum bounce angle
        MIN_BOUNCE: 15, // Minimum bounce angle
        BOUNCE_RATIO: 0.6, // Ratio for the backward bounce
        RETURN_THRESHOLD: 250, // Speed threshold for returning to center
    },
    SLOW_SPEED: {
        FORWARD_ANGLE: 20, // Forward bounce angle
        BACKWARD_ANGLE: 15, // Backward bounce angle
    },

    // Animation timing (ms)
    HIGH_SPEED_TIMING: 50,
    MEDIUM_SPEED_TIMING: { FIRST: 60, SECOND: 120 },
    SLOW_SPEED_TIMING: { FIRST: 80, SECOND: 160 },
};

const maxWidth = 3200;

const BlockPairingWheel = ({
    __typename,
    entryTitle,
    eyebrow,
    headline,
    copy,
    image,
    items,
    revealHeadline,
    revealCopy,
    viewGuideCta,
    pairAgainCtaText,
    revealImage,
    primaryColor = '#f28972',
    secondaryColor = '#F5A089',
    loadingIcon,
    loadingMessage1,
    loadingMessage2,
    loadingMessage3,
    loadingMessage4,
    spinDirection = 'clockwise', // 'counterClockwise' or 'clockwise'
    nextPairingWheel,
}) => {
    const isLarge = useMediaQuery('(min-width: 768px)', false);
    const rootRef = useRef(null);
    const wheelSpinRef = useRef(null);
    const wheelClapperRef = useRef(null);
    const [wheelWidth, setWheelWidth] = useState(null);
    const [wheelOuter, setWheelOuter] = useState(null);
    const [wheelInner, setWheelInner] = useState(null);
    const [wheelBorderRadius, setWheelBorderRadius] = useState(null);
    const [wheelBorderWidth, setWheelBorderWidth] = useState(null);
    const [spunSections, setSpunSections] = useState([]);
    const [uniqueSpins, setUniqueSpins] = useState(0);
    const [totalSpins, setTotalSpins] = useState(0);
    const [spinsAfterCompletion, setSpinsAfterCompletion] = useState(0);
    const [currentContentSection, setCurrentContentSection] = useState(null);
    const [isSpinning, setIsSpinning] = useState(false);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const [isRevealOverlayOpen, setIsRevealOverlayOpen] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [nextPairingWheelId, setNextPairingWheelId] = useState(
        nextPairingWheel?.sys?.id
    );
    const [wheelData, setWheelData] = useState({
        entryTitle,
        eyebrow,
        headline,
        copy,
        image,
        items,
        revealHeadline,
        revealCopy,
        viewGuideCta,
        pairAgainCtaText,
        revealImage,
        primaryColor,
        secondaryColor,
        loadingIcon,
        loadingMessage1,
        loadingMessage2,
        loadingMessage3,
        loadingMessage4,
    });

    const _primaryColor = getColor(wheelData.primaryColor || primaryColor);
    const _secondaryColor = getColor(
        wheelData.secondaryColor || secondaryColor
    );

    const isSpinsComplete = uniqueSpins === 8;

    const rotationRef = useRef(0);

    // Add spring animation for the clapper
    const springConfig = {
        stiffness: CLAPPER_CONFIG.SPRING_STIFFNESS,
        damping: CLAPPER_CONFIG.SPRING_DAMPING,
    };
    const clapperSpring = useSpring(0, springConfig);

    // Track wheel speed for realistic clapper behavior
    const lastRotationRef = useRef(0);
    const lastTimeRef = useRef(Date.now());

    // Keep track of current section for clapper animation
    const lastSectionRef = useRef(-1);

    // Refs for timers to allow proper cleanup
    const timer1Ref = useRef(null);
    const timer2Ref = useRef(null);

    // Setup framer-motion controls
    const controls = useAnimation();

    const idleIntervalRef = useRef(null);
    const idleTimeoutRef = useRef(null);
    const idleStopTimeoutRef = useRef(null);

    // Calculate the target rotation based on section number (8 sections total)
    const calculateTargetRotation = sectionIndex => {
        // Each section is 45 degrees (360 / 8)
        const currentRotation = rotationRef.current || 0;
        const currentAngle = currentRotation % 360; // Normalize to 0-359
        const targetAngle = -sectionIndex * 45; // The target angle we want to end at

        // Direction multiplier: -1 for counter-clockwise, 1 for clockwise
        const directionMultiplier = spinDirection === 'clockwise' ? 1 : -1;

        // Calculate how many degrees to rotate based on direction
        let degreesToRotate;

        // If we're already at the target angle, do a full 360
        if (currentAngle === targetAngle) {
            degreesToRotate = 360;
        } else {
            if (spinDirection === 'clockwise') {
                // Calculate the clockwise distance between angles
                degreesToRotate =
                    currentAngle < targetAngle
                        ? targetAngle - currentAngle
                        : 360 - currentAngle + targetAngle;
            } else {
                // Calculate the counter-clockwise distance between angles (original logic)
                degreesToRotate =
                    currentAngle > targetAngle
                        ? currentAngle - targetAngle
                        : currentAngle + (360 - targetAngle);
            }
        }

        // Always add 2 full rotations (720 degrees) for a good spinning effect
        // Apply direction multiplier to create clockwise or counter-clockwise rotation
        return (
            currentRotation +
            directionMultiplier * degreesToRotate +
            directionMultiplier * 720
        );
    };

    const handleSpinClick = async () => {
        // Don't allow spinning while animation is in progress
        if (isSpinning) return;

        // Set spinning state immediately to prevent multiple clicks
        setIsSpinning(true);

        // Mark that the user has interacted with the wheel
        setHasUserInteracted(true);

        // Ensure all idle animations are completely stopped
        if (idleIntervalRef.current) {
            clearInterval(idleIntervalRef.current);
            idleIntervalRef.current = null;
        }
        if (idleTimeoutRef.current) {
            clearTimeout(idleTimeoutRef.current);
            idleTimeoutRef.current = null;
        }
        if (idleStopTimeoutRef.current) {
            clearTimeout(idleStopTimeoutRef.current);
            idleStopTimeoutRef.current = null;
        }

        // Force-stop any ongoing animations and reset animation state
        // Wait a tiny bit for the animation system to clear
        await controls.stop();
        await new Promise(resolve => setTimeout(resolve, 10));

        // Generate random section number between 0-7
        let randomSection;

        // If all 8 sections have been completed, go back to pure random
        if (spunSections.length === 8) {
            randomSection = Math.floor(Math.random() * 8);
        } else {
            // If not all sections completed, avoid already spun sections
            do {
                randomSection = Math.floor(Math.random() * 8);
            } while (spunSections.includes(randomSection));
        }

        // Increment total spins counter
        const newTotalSpins = totalSpins + 1;
        setTotalSpins(newTotalSpins);

        // If the wheel is already complete, track spins after completion
        if (uniqueSpins === 8) {
            setSpinsAfterCompletion(spinsAfterCompletion + 1);
        }

        // Set current content section
        setCurrentContentSection(randomSection);

        // Calculate target rotation
        const targetRotation = calculateTargetRotation(randomSection);

        // First animate to overshoot the target a bit
        const overshootAmount = 4; // Degrees to overshoot
        const directionMultiplier = spinDirection === 'clockwise' ? 1 : -1;
        const overshootRotation =
            targetRotation + directionMultiplier * overshootAmount;

        try {
            // Animate the wheel - 2 full rotations plus the angle to land on the section with overshoot
            await controls.start({
                rotate: overshootRotation,
                transition: {
                    duration: 2.8,
                    ease: [0.2, 0.0, 0.1, 1], // Modified ease for initial spin
                },
            });

            // Check if this is a new section
            if (!spunSections.includes(randomSection)) {
                const updatedSections = [...spunSections, randomSection];
                setSpunSections(updatedSections);
                setUniqueSpins(updatedSections.length);
            }

            // Then animate back to the actual target (rubberband effect)
            await controls.start({
                rotate: targetRotation,
                transition: {
                    type: 'spring',
                    duration: 0.5,
                    bounce: 0.3,
                    damping: 12,
                    stiffness: 150,
                },
            });

            // Only after the bounce completes, finish the rotation
            rotationRef.current = targetRotation; // Update the current rotation to the new position
        } catch (error) {
            console.error('Animation error:', error);
        } finally {
            // Always ensure we reset the spinning state, even if there was an error
            setIsSpinning(false);
        }
    };

    const handleRevealClick = () => {
        setIsRevealOverlayOpen(true);
    };

    const handlePairAgainClick = async () => {
        // Only proceed if we have a nextPairingWheelId to fetch
        if (!nextPairingWheelId) {
            console.error('No next pairing wheel ID available');
            return;
        }

        // Clear any existing timers to prevent memory leaks or multiple executions
        if (timer1Ref.current) {
            clearTimeout(timer1Ref.current);
            timer1Ref.current = null;
        }

        if (timer2Ref.current) {
            clearTimeout(timer2Ref.current);
            timer2Ref.current = null;
        }

        try {
            // Start loading state - fade out
            setIsLoading(true);

            // Fetch the next pairing wheel directly from Contentful
            const {
                body: {
                    data: { blockPairingWheel: nextWheel },
                },
            } = await graphRequest({
                query: pairingWheelQuery,
                variables: { id: nextPairingWheelId, preview: false },
            });

            // Reset the state for the new wheel
            setIsRevealOverlayOpen(false);
            setCurrentContentSection(null);
            setSpunSections([]);
            setUniqueSpins(0);
            setTotalSpins(0);
            setSpinsAfterCompletion(0);
            setHasUserInteracted(false);

            // Small delay before updating the wheel data to ensure fade out is complete
            timer1Ref.current = setTimeout(() => {
                rotationRef.current = 0;
                controls.set({ rotate: 0 });
                lastSectionRef.current = -1;

                // Update the wheel data with the next pairing wheel
                setWheelData({
                    entryTitle: nextWheel.entryTitle,
                    eyebrow: nextWheel.eyebrow,
                    headline: nextWheel.headline,
                    copy: nextWheel.copy,
                    image: nextWheel.image,
                    items: nextWheel.itemsCollection?.items || [],
                    revealHeadline: nextWheel.revealHeadline,
                    revealCopy: nextWheel.revealCopy,
                    viewGuideCta: nextWheel.viewGuideCta,
                    pairAgainCtaText: nextWheel.pairAgainCtaText,
                    revealImage: nextWheel.revealImage,
                    primaryColor: nextWheel.primaryColor,
                    secondaryColor: nextWheel.secondaryColor,
                    loadingIcon: nextWheel.loadingIcon,
                    loadingMessage1: nextWheel.loadingMessage1,
                    loadingMessage2: nextWheel.loadingMessage2,
                    loadingMessage3: nextWheel.loadingMessage3,
                    loadingMessage4: nextWheel.loadingMessage4,
                });

                // Store the next pairing wheel ID in state for the next click
                setNextPairingWheelId(nextWheel.nextPairingWheel?.sys?.id);

                // End loading state - fade in
                timer2Ref.current = setTimeout(() => {
                    setIsLoading(false);
                    clapperSpring.set(0);
                    timer2Ref.current = null;
                }, 1000);

                timer1Ref.current = null;
            }, 1000);
        } catch (error) {
            console.error('Error fetching next pairing wheel:', error);

            // Try to extract more details about the error
            if (error.response) {
                try {
                    const errorData = await error.response.json();
                    console.error('Error response details:', errorData);
                } catch {
                    console.error('Could not parse error response');
                }
            }

            // Clear any timers in case of an error
            if (timer1Ref.current) {
                clearTimeout(timer1Ref.current);
                timer1Ref.current = null;
            }

            if (timer2Ref.current) {
                clearTimeout(timer2Ref.current);
                timer2Ref.current = null;
            }

            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (nextPairingWheel?.sys?.id) {
            setNextPairingWheelId(nextPairingWheel.sys.id);
        }
    }, [nextPairingWheel]);

    // Clean up timers when component unmounts
    useEffect(() => {
        return () => {
            // Clear timers to prevent memory leaks when component unmounts
            if (timer1Ref.current) {
                clearTimeout(timer1Ref.current);
            }

            if (timer2Ref.current) {
                clearTimeout(timer2Ref.current);
            }
            controls.stop();
        };
    }, [controls]);

    // Create a ref to track the idle animation state across renders
    const isIdleAnimationRunningRef = useRef(false);

    // Add idle animation for the wheel
    // This will rotate the wheel one section every 2 seconds when no user interaction has occurred
    useEffect(() => {
        // Only start idle animation if component is ready and user hasn't interacted yet
        if (isReady && !hasUserInteracted && !isSpinning && !isLoading) {
            // Clean up any previously running animations
            if (idleIntervalRef.current) {
                clearInterval(idleIntervalRef.current);
                idleIntervalRef.current = null;
            }
            if (idleTimeoutRef.current) {
                clearTimeout(idleTimeoutRef.current);
                idleTimeoutRef.current = null;
            }
            if (idleStopTimeoutRef.current) {
                clearTimeout(idleStopTimeoutRef.current);
                idleStopTimeoutRef.current = null;
            }

            // Reset idle animation state
            isIdleAnimationRunningRef.current = false;

            // Small initial delay to let the component fully render
            idleTimeoutRef.current = setTimeout(() => {
                let currentIdleSection = 0;

                // Start the idle animation interval
                idleIntervalRef.current = setInterval(() => {
                    // Don't start a new animation if one is already running or if user is interacting
                    if (
                        isIdleAnimationRunningRef.current ||
                        isSpinning ||
                        hasUserInteracted
                    ) {
                        return;
                    }

                    // Flag that an idle animation is running
                    isIdleAnimationRunningRef.current = true;

                    // Calculate next section (one at a time)
                    currentIdleSection = (currentIdleSection + 1) % 8;

                    // Calculate rotation to next section (one section = 45 degrees)
                    const directionMultiplier =
                        spinDirection === 'clockwise' ? 1 : -1;
                    const currentRotation = rotationRef.current || 0;
                    const targetRotation =
                        currentRotation + directionMultiplier * 45;

                    // Add a small overshoot for bounce-back effect
                    const overshootAmount = 2; // Smaller overshoot for idle animation
                    const overshootRotation =
                        targetRotation + directionMultiplier * overshootAmount;

                    // Function to safely run idle animation
                    const runIdleAnimation = async () => {
                        try {
                            // First animate to the overshoot position
                            await controls.start({
                                rotate: overshootRotation,
                                transition: {
                                    duration: 0.6,
                                    ease: [0.25, 0.1, 0.25, 1], // Custom ease for smooth acceleration
                                },
                            });

                            // Check again if we should continue with the animation
                            if (hasUserInteracted || isSpinning) {
                                isIdleAnimationRunningRef.current = false;
                                return;
                            }

                            // Then animate back to the actual target (bounce-back effect)
                            await controls.start({
                                rotate: targetRotation,
                                transition: {
                                    type: 'spring',
                                    duration: 0.1,
                                    bounce: 0.2,
                                    damping: 16,
                                    stiffness: 250,
                                },
                            });

                            // Update rotation reference after bounce is complete
                            if (!hasUserInteracted && !isSpinning) {
                                rotationRef.current = targetRotation;
                            }
                        } catch (error) {
                            console.error('Idle animation error:', error);
                        } finally {
                            // Always ensure we reset the flag when done, even if there was an error
                            isIdleAnimationRunningRef.current = false;
                        }
                    };

                    // Start the idle animation
                    runIdleAnimation();
                }, 3000);
            }, 500);

            // WCAG 2.2.2: idle motion must stop within 5s
            idleStopTimeoutRef.current = setTimeout(() => {
                clearInterval(idleIntervalRef.current);
                idleIntervalRef.current = null;
            }, 5000);
        }

        // Clean up the interval and timeout when component unmounts or state changes
        return () => {
            if (idleIntervalRef.current) {
                clearInterval(idleIntervalRef.current);
                idleIntervalRef.current = null;
            }
            if (idleTimeoutRef.current) {
                clearTimeout(idleTimeoutRef.current);
                idleTimeoutRef.current = null;
            }
            if (idleStopTimeoutRef.current) {
                clearTimeout(idleStopTimeoutRef.current);
                idleStopTimeoutRef.current = null;
            }
            controls.stop();
        };
    }, [
        isReady,
        hasUserInteracted,
        isSpinning,
        isLoading,
        spinDirection,
        controls,
    ]);

    useEffect(() => {
        const updateSizes = () => {
            // Apply maxWidth constraint to prevent the wheel from getting too big
            const viewportWidth = Math.min(window.innerWidth, maxWidth / 2);

            const borderRadius = isLarge ? 20 : 10;
            const borderWidth = isLarge ? 10 : 5;

            const widthMulti = isLarge ? 2 : 2;
            const outerMutli = isLarge ? 0.46 : 0.86;
            const innerMulti = isLarge ? 0.23 : 0.42;

            setWheelWidth(viewportWidth * widthMulti);
            setWheelOuter(viewportWidth * outerMutli);
            setWheelInner(viewportWidth * innerMulti);
            setWheelBorderRadius(borderRadius);
            setWheelBorderWidth(borderWidth);
        };

        updateSizes();
        const resizeObserver = new ResizeObserver(() => {
            updateSizes();
        });

        const currentRef = rootRef.current;

        if (currentRef) {
            resizeObserver.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                resizeObserver.unobserve(currentRef);
            }
            resizeObserver.disconnect();
        };
    }, [isLarge]);

    const handleSvgRendered = () => {
        setIsReady(true);
    };

    return (
        <div
            {...blockRootProps(null, __typename)}
            className={cx(styles.root, {
                [styles.isReady]: isReady,
                [styles.isSpinsComplete]: isSpinsComplete,
                [styles.isLoading]: isLoading,
            })}
            ref={rootRef}
            style={{
                '--primary-color': _primaryColor,
                '--secondary-color': _secondaryColor,
            }}
        >
            <div className={styles.inset}>
                <div className={styles.loading}>
                    <Spinner />
                </div>
                <div className={styles.inner}>
                    <div className={styles.content}>
                        <PairingWheelContent
                            eyebrow={wheelData.eyebrow}
                            headline={wheelData.headline}
                            copy={wheelData.copy}
                            items={wheelData.items}
                            loadingIcon={wheelData.loadingIcon}
                            loadingMessage1={wheelData.loadingMessage1}
                            loadingMessage2={wheelData.loadingMessage2}
                            loadingMessage3={wheelData.loadingMessage3}
                            loadingMessage4={wheelData.loadingMessage4}
                            handleSpinClick={handleSpinClick}
                            handleRevealClick={handleRevealClick}
                            uniqueSpins={uniqueSpins}
                            isSpinsComplete={isSpinsComplete}
                            currentContentSection={currentContentSection}
                        />
                    </div>
                    <div className={styles.wheel}>
                        <div className={styles.wheelClapperContainer}>
                            <m.div
                                ref={wheelClapperRef}
                                className={styles.wheelClapper}
                                style={{ rotate: clapperSpring }}
                            >
                                <svg
                                    width="30"
                                    height="29"
                                    viewBox="0 0 30 29"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M23.9736 0.0400394C27.7504 0.0400394 30.1838 4.04277 28.4452 7.39571L19.1013 25.4161C17.2219 29.0407 12.0374 29.0407 10.158 25.4161L0.814055 7.39572C-0.924504 4.04278 1.50883 0.0400391 5.2857 0.0400392L23.9736 0.0400394Z"
                                        fill="#001E60"
                                    />
                                </svg>
                            </m.div>
                        </div>
                        {wheelWidth && items?.length === 8 && (
                            <div
                                className={styles.wheelSize}
                                style={{
                                    width: wheelWidth,
                                    height: wheelWidth,
                                }}
                            >
                                <m.div
                                    className={styles.wheelRotate}
                                    ref={wheelSpinRef}
                                    animate={controls}
                                    initial={{ rotate: 0 }}
                                    onUpdate={latest => {
                                        // Calculate which section is hitting the clapper
                                        const currentRotation = latest.rotate;

                                        // Calculate rotation speed
                                        const now = Date.now();
                                        const timeDelta =
                                            now - lastTimeRef.current;
                                        const rotationDelta = Math.abs(
                                            currentRotation -
                                                lastRotationRef.current
                                        );
                                        // Calculate speed in degrees per second
                                        const currentSpeed =
                                            timeDelta > 0
                                                ? rotationDelta /
                                                  (timeDelta / 1000)
                                                : 0;

                                        // Update our tracking variables
                                        lastTimeRef.current = now;
                                        lastRotationRef.current =
                                            currentRotation;
                                        rotationRef.current = currentRotation;

                                        // Calculate section based on rotation (8 sections = 45 degrees each)
                                        // Adding 22.5 to center the hit on the middle of each section
                                        const normalizedRotation =
                                            ((currentRotation % 360) + 360) %
                                            360;
                                        const currentSection =
                                            Math.floor(
                                                (normalizedRotation + 22.5) / 45
                                            ) % 8; // Get the speed thresholds from config
                                        const {
                                            HIGH_SPEED_THRESHOLD,
                                            MEDIUM_SPEED_THRESHOLD,
                                        } = CLAPPER_CONFIG;

                                        if (
                                            currentSpeed > HIGH_SPEED_THRESHOLD
                                        ) {
                                            // At high speeds, the clapper gets pushed to one side and stays there
                                            // with small bobbles as each section passes

                                            // Determine the direction of rotation to know which way to push clapper
                                            const rotationDirection =
                                                spinDirection === 'clockwise'
                                                    ? 1
                                                    : -1;

                                            // Set the base position of the clapper when pushed to one side
                                            const basePosition =
                                                rotationDirection *
                                                -CLAPPER_CONFIG.HIGH_SPEED
                                                    .BASE_POSITION;

                                            // Only trigger clapper bobble animation when we hit a new section
                                            if (
                                                currentSection !==
                                                lastSectionRef.current
                                            ) {
                                                lastSectionRef.current =
                                                    currentSection;

                                                // Small bobble effect - briefly move the clapper a bit more
                                                // then return to the pushed position
                                                clapperSpring.set(
                                                    basePosition -
                                                        rotationDirection *
                                                            CLAPPER_CONFIG
                                                                .HIGH_SPEED
                                                                .BOBBLE_AMOUNT
                                                );
                                                setTimeout(() => {
                                                    clapperSpring.set(
                                                        basePosition
                                                    );
                                                }, CLAPPER_CONFIG.HIGH_SPEED_TIMING);
                                            }
                                        } else if (
                                            currentSpeed >
                                            MEDIUM_SPEED_THRESHOLD
                                        ) {
                                            // At medium speeds, the clapper can bounce back but not fully

                                            // Only trigger animation on section change
                                            if (
                                                currentSection !==
                                                lastSectionRef.current
                                            ) {
                                                lastSectionRef.current =
                                                    currentSection;

                                                // Calculate bounce intensity based on speed
                                                // As speed decreases, we want more pronounced bounces
                                                const {
                                                    MAX_BOUNCE,
                                                    MIN_BOUNCE,
                                                    BOUNCE_RATIO,
                                                    RETURN_THRESHOLD,
                                                } = CLAPPER_CONFIG.MEDIUM_SPEED;

                                                const bounceIntensity =
                                                    Math.min(
                                                        MAX_BOUNCE,
                                                        Math.max(
                                                            MIN_BOUNCE,
                                                            35 -
                                                                currentSpeed /
                                                                    15
                                                        )
                                                    );
                                                const returnPosition =
                                                    currentSpeed >
                                                    RETURN_THRESHOLD
                                                        ? -10
                                                        : 0;

                                                // Trigger the clapper spring animation
                                                clapperSpring.set(
                                                    bounceIntensity
                                                );
                                                setTimeout(() => {
                                                    clapperSpring.set(
                                                        -bounceIntensity *
                                                            BOUNCE_RATIO
                                                    );
                                                }, CLAPPER_CONFIG.MEDIUM_SPEED_TIMING.FIRST);
                                                setTimeout(() => {
                                                    clapperSpring.set(
                                                        returnPosition
                                                    );
                                                }, CLAPPER_CONFIG.MEDIUM_SPEED_TIMING.SECOND);
                                            }
                                        } else {
                                            // At slow speeds, the clapper can fully bounce back

                                            // Standard clapper behavior for slow speeds
                                            if (
                                                currentSection !==
                                                lastSectionRef.current
                                            ) {
                                                lastSectionRef.current =
                                                    currentSection;

                                                const {
                                                    FORWARD_ANGLE,
                                                    BACKWARD_ANGLE,
                                                } = CLAPPER_CONFIG.SLOW_SPEED;
                                                const { FIRST, SECOND } =
                                                    CLAPPER_CONFIG.SLOW_SPEED_TIMING;

                                                // Full bounce animation
                                                clapperSpring.set(
                                                    FORWARD_ANGLE
                                                );
                                                setTimeout(() => {
                                                    clapperSpring.set(
                                                        -BACKWARD_ANGLE
                                                    );
                                                }, FIRST);
                                                setTimeout(() => {
                                                    clapperSpring.set(0);
                                                }, SECOND);
                                            }
                                        }
                                    }}
                                >
                                    <PairingWheel
                                        outerRadius={wheelOuter}
                                        innerRadius={wheelInner}
                                        borderRadius={wheelBorderRadius}
                                        borderThickness={wheelBorderWidth}
                                        primaryColor={_primaryColor}
                                        secondaryColor={_secondaryColor}
                                        visitedSections={spunSections}
                                        items={wheelData.items}
                                        onSvgRendered={handleSvgRendered}
                                        isLarge={isLarge}
                                    />
                                </m.div>
                            </div>
                        )}
                        {wheelData.image?.url && (
                            <div className={styles.imageContainer}>
                                <NextImage
                                    className={styles.image}
                                    src={addProtocol(wheelData.image.url)}
                                    width={wheelData.image.width}
                                    height={wheelData.image.height}
                                    sizes="(max-width: 768px) 66vw, 33vw"
                                    alt={
                                        wheelData.image.title ||
                                        wheelData.image.description ||
                                        ''
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>
                <PairingWheelOverlay
                    isOpen={isRevealOverlayOpen}
                    onClose={() => setIsRevealOverlayOpen(false)}
                    headline={wheelData.revealHeadline}
                    copy={wheelData.revealCopy}
                    image={wheelData.revealImage}
                    viewGuideCta={wheelData.viewGuideCta}
                    pairAgainCtaText={wheelData.pairAgainCtaText}
                    handlePairAgainClick={handlePairAgainClick}
                    nextPairingWheelId={nextPairingWheelId}
                />
            </div>
        </div>
    );
};

BlockPairingWheel.propTypes = {
    __typename: PropTypes.string,
    entryTitle: PropTypes.string,
    eyebrow: PropTypes.string,
    headline: PropTypes.string,
    copy: PropTypes.string,
    image: PropTypes.object,
    items: PropTypes.arrayOf(PropTypes.object),
    revealHeadline: PropTypes.string,
    revealCopy: PropTypes.string,
    viewGuideCta: PropTypes.object,
    pairAgainCtaText: PropTypes.string,
    revealImage: PropTypes.object,
    primaryColor: PropTypes.string,
    secondaryColor: PropTypes.string,
    loadingIcon: PropTypes.object,
    loadingMessage1: PropTypes.string,
    loadingMessage2: PropTypes.string,
    loadingMessage3: PropTypes.string,
    loadingMessage4: PropTypes.string,
    spinDirection: PropTypes.oneOf(['clockwise', 'counterClockwise']),
    nextPairingWheelId: PropTypes.string,
};

export default BlockPairingWheel;
